import { useState, useEffect, useContext, createContext } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { CurrentTime, TimeStringCalculate, TokenExpireTime } from "../utils";
import ARENO_API_ENDPOINT from "../api";
import { BASE_PATH_TO_AUTH } from "../base_routes_paths/auth_paths";
import Loader from "../components/Loader/Loader";

const AuthContext = createContext();

const retrieveStoredToken = () => {
  const storedToken = localStorage.getItem("token");

  return {
    token: storedToken,
  };
};

function extractEnvironment() {
  let url = ARENO_API_ENDPOINT;

  const startIndex = url.indexOf("mobileapi-") + "mobileapi-".length;
  const endIndex = url.indexOf(".areno.in");

  if (startIndex >= 0 && endIndex > startIndex) {
    return url.substring(startIndex, endIndex);
  } else {
    return "prod";
  }
}

const fileName = extractEnvironment();

const retrieveUserName = () => {
  const storedName = localStorage.getItem("userName");
  return {
    name: storedName,
  };
};

const retrieveUserImg = () => {
  const storedImage = localStorage.getItem("profileImg");
  return {
    img: storedImage,
  };
};

export const AuthContextProvider = (props) => {
  const tokenData = retrieveStoredToken();
  const userName = retrieveUserName();
  const userImg = retrieveUserImg();
  const navigate = useNavigate();
  const currentDateTime = new Date();

  let initialToken;
  let name;
  let profileImg;
  if (tokenData && userName && userImg) {
    name = userName.name;
    initialToken = tokenData.token;
    profileImg =
      userImg && userImg.img !== null && userImg.img !== undefined
        ? userImg.img
        : "https://www.areno.in/images/logo.png";
  }

  const [token, setToken] = useState(initialToken);
  const [loading, setLoading] = useState(true);
  const [apiCallCheck, setApiCallCheck] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const userIsLoggedIn = !!token;

  const logoutHandler = () => {
    setToken(null);
    setLoading(false); 
    localStorage.removeItem("userName");
    localStorage.removeItem("tokenExpiresIn");
    localStorage.removeItem("profileImg");
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("TimeOffSet");

    // Clear all sessionStorage cache (widget and chart data)
    sessionStorage.clear();

    toast.success("Logged out successfully!!", {
      position: "bottom-left",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });
    navigate(BASE_PATH_TO_AUTH);
  };

  const loginHandler = (token, refreshToken) => {
    setToken(token);
    localStorage.setItem("token", token);
    localStorage.setItem("refreshToken", refreshToken);
  };

  const updateToken = async () => {
    if (isRefreshing) {
      console.log("Token refresh already in progress, skipping...");
      return;
    }

    const refreshToken = localStorage.getItem("refreshToken");
    console.log(
      "Attempting to refresh token with refreshToken:",
      refreshToken ? "exists" : "missing"
    );

    if (
      !refreshToken ||
      refreshToken === "undefined" ||
      refreshToken === undefined
    ) {
      console.log("No valid refresh token found, logging out");
      logoutHandler();
      return;
    }

    try {
      setIsRefreshing(true);
      console.log("Making refresh token API call...");
      let response = await fetch(`${ARENO_API_ENDPOINT}/auth/RefreshToken`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${refreshToken}`,
        },
      });

      let data = await response.json();
      console.log("Refresh token API response:", response.status, data);

      if (response.status === 200 && data.data) {
        console.log("Token refresh successful, updating tokens...");
        setToken(data.data.token);
        loginHandler(data.data.token, data.data.refreshToken);
        localStorage.setItem(
          "tokenExpiresIn",
          TokenExpireTime(currentDateTime, parseInt(data.data.tokenExpiresIn))
        );

        console.log("Token updated successfully");
        setLoading(false);
      } else {
        console.log("Token refresh failed, logging out");
        logoutHandler();
      }
    } catch (error) {
      console.error("Error during token refresh:", error);
      logoutHandler();
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    console.log(
      "Auth useEffect triggered, localStorage length:",
      localStorage.length
    );

    if (localStorage.length === 0) {
      console.log("No localStorage data, setting loading to false");
      setLoading(false);
      return;
    }

    const refreshToken = localStorage.getItem("refreshToken");
    const tokenExpiresIn = localStorage.getItem("tokenExpiresIn");

    console.log("Checking token expiration:", {
      refreshToken: refreshToken ? "exists" : "missing",
      tokenExpiresIn: tokenExpiresIn ? tokenExpiresIn : "missing",
      currentToken: token ? "exists" : "missing",
    });

    if (!refreshToken || refreshToken === "undefined") {
      console.log("Invalid refresh token, logging out");
      logoutHandler();
      return;
    }

    if (!tokenExpiresIn) {
      console.log("No token expiration time found, logging out");
      logoutHandler();
      return;
    }

    try {
      const timestamp1String = CurrentTime();
      const timestamp2String = tokenExpiresIn;

      const timestamp1 = TimeStringCalculate(timestamp1String);
      const timestamp2 = TimeStringCalculate(timestamp2String);

      const timeDifference = timestamp1 - timestamp2;

      console.log("Time comparison:", {
        currentTime: timestamp1String,
        expirationTime: timestamp2String,
        timeDifference: timeDifference,
        isExpired: timeDifference > 0,
        timeUntilExpiration:
          Math.round((timestamp2 - timestamp1) / 1000) + " seconds",
      });

      // If token has expired (positive timeDifference means current time > expiration time)
      if (timeDifference > 0) {
        console.log("Token expired, attempting refresh");
        if (token) {
          updateToken();
        } else {
          console.log("No current token, logging out");
          logoutHandler();
        }
      } else {
        console.log("Token still valid, setting loading to false");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error in token expiration check:", error);
      logoutHandler();
    }
  }, [navigate, token]);

  const checkAndRefreshToken = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    const tokenExpiresIn = localStorage.getItem("tokenExpiresIn");

    if (!refreshToken || refreshToken === "undefined" || !tokenExpiresIn) {
      return false;
    }

    try {
      const timestamp1String = CurrentTime();
      const timestamp1 = TimeStringCalculate(timestamp1String);
      const timestamp2 = TimeStringCalculate(tokenExpiresIn);
      const timeDifference = timestamp1 - timestamp2;

      // If token expires in the next 5 minutes (300000ms), refresh it
      if (timeDifference > -300000) {
        console.log("Token expiring soon, refreshing preemptively");
        await updateToken();
        return true;
      }
    } catch (error) {
      console.error("Error checking token expiration:", error);
      return false;
    }

    return false;
  };

  const contextValue = {
    user: name,
    profileImg: profileImg,
    token: token,
    isLoggedIn: userIsLoggedIn,
    login: loginHandler,
    logout: logoutHandler,
    apiCallCheck: apiCallCheck,
    setApiCallCheck: setApiCallCheck,
    fileName: fileName,
    checkAndRefreshToken: checkAndRefreshToken,
    updateToken: updateToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {loading ? <Loader /> : props.children}
    </AuthContext.Provider>
  );
};

const AUTH_CONTEXT = () => {
  return useContext(AuthContext);
};

export default AUTH_CONTEXT;
